// src/pages/TeamPendingTasks.jsx
//
// One team's pending backlog, as a screen.
//
// Reached from the dashboard's workload panel — the PENDING number on a team
// card, and the "no team yet" line above them. Which team is in the URL rather
// than in state, so the tab survives a refresh, the link can be sent to
// somebody, and the browser's back button does what it looks like it does.
//
// The list itself is TeamPendingTasksView; this file only works out WHICH team
// and puts a frame around it.
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import TeamPendingTasksView from "../components/TeamPendingTasksView";
import { teamListApis } from "../redux/team/teamActions";
import { selectTeams } from "../redux/team/teamSelectors";
import "../assets/css/teamPending.css";

/* Which team was last looked at, so the tab can be returned to.
 *
 * Tabs are keyed on the PATHNAME alone (layouts/screens.js), so clicking this
 * screen's own tab navigates to a bare /team-pending-tasks with the team
 * stripped off — and without this the tab you just came back to would be
 * empty. Session-scoped rather than local: it is where you were in THIS
 * sitting, not a preference. */
const LAST_KEY = "skyeye.teamPending.last";
const PATH = "/team-pending-tasks";

const TeamPendingTasks = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const search = params.toString();

  const teamList = useSelector(selectTeams);

  /* Is this screen the one being LOOKED at?
   *
   * Every open tab stays mounted — that is the point of the tab bar, and it is
   * what lets filters and scroll positions survive a switch. The catch is that
   * a hidden screen still sees the live URL: switch to Dashboard and this
   * component is still running, but useSearchParams now reports the
   * DASHBOARD's query string.
   *
   * Without this guard the restore effect below read that empty query as "the
   * team has fallen off my own URL", navigated back to /team-pending-tasks,
   * and made the Dashboard and Development Tasks tabs impossible to open — one
   * click and you bounced straight back here.
   *
   * Any effect that reads the URL and then ACTS on it needs the same guard. */
  const isActive = pathname === PATH;

  /* Remembered on the way in, put back on the way back. Both wrapped: storage
     throws outright in some privacy modes, and a screen that will not render
     because it could not write a breadcrumb is a worse trade than losing the
     breadcrumb. */
  useEffect(() => {
    if (!isActive || !search) return;
    try {
      sessionStorage.setItem(LAST_KEY, search);
    } catch {
      /* nothing to do — the page still works, it just will not remember. */
    }
  }, [isActive, search]);

  useEffect(() => {
    if (!isActive || search) return;

    let last = "";
    try {
      last = sessionStorage.getItem(LAST_KEY) || "";
    } catch {
      last = "";
    }

    /* replace, not push: this is restoring where you already were, and a
       pushed entry would make Back bounce between the bare path and the
       filled one. */
    if (last) navigate(`${PATH}?${last}`, { replace: true });
  }, [isActive, search, navigate]);

  const noTeam = params.get("noTeam") === "1";
  const rawCode = Number(params.get("teamCode"));
  const teamCode = Number.isInteger(rawCode) && rawCode > 0 ? rawCode : null;

  /* The names travel in the URL as well as the code.
   *
   * Not as the source of truth — the code is that — but so the heading is
   * right on the first paint instead of appearing a beat later, and so a
   * deep link still says which team it is while the list loads. A rename
   * would make them stale, which is why the live list wins wherever it can
   * answer. */
  const hintedName = params.get("name") || "";
  const hintedShort = params.get("short") || "";

  useEffect(() => {
    /* Only when there is nothing to read. Arriving from the dashboard the
       teams are already in the store, and this page is usually opened from
       there — but a refresh or a pasted link starts with an empty one. */
    if (!noTeam && teamCode && !(Array.isArray(teamList) && teamList.length)) {
      dispatch(teamListApis({}));
    }
  }, [dispatch, noTeam, teamCode, teamList]);

  /* What the view needs is a { TeamCode, TeamName, ShortName } — the same
     shape the dashboard card passes it. "No Team" is not a row in tbl_Team, so
     it is built here; the view already reads a null TeamCode as that case. */
  const team = useMemo(() => {
    if (noTeam) {
      return { TeamCode: null, TeamName: "No Team", ShortName: "--" };
    }

    if (!teamCode) return null;

    const list = Array.isArray(teamList) ? teamList : teamList?.data || [];
    const found = list.find((t) => Number(t.TeamCode) === teamCode);

    return {
      TeamCode: teamCode,
      TeamName: found?.TeamName || hintedName || `Team ${teamCode}`,
      ShortName: found?.ShortName || hintedShort || "--",
    };
  }, [noTeam, teamCode, teamList, hintedName, hintedShort]);

  /* A link with no team in it at all. Rather than an empty screen, say so and
     offer the way back — this is only reachable by editing the URL or by
     following a link written before the parameters settled. */
  if (!team) {
    return (
      <div className="report-container">
        <div className="tpt-page__empty">
          <i className="bi bi-diagram-3" aria-hidden="true" />
          <p>No team in the link, so there is nothing to list.</p>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/dashboard")}
          >
            Open the dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="report-container">
      {/* Back to where this was opened from. The dashboard is its own tab and
          stays mounted, so this returns to the panel as it was left rather
          than reloading it. */}
      <button
        type="button"
        className="tpt-page__back"
        onClick={() => navigate("/dashboard")}
      >
        <i className="bi bi-arrow-left" aria-hidden="true" />
        Dashboard
      </button>

      <TeamPendingTasksView team={team} />
    </div>
  );
};

export default TeamPendingTasks;
